import React, { useEffect, useState, useLayoutEffect } from "react";
import { Button, Card, CardBody, Table, Input } from "reactstrap";
import { useNavigate } from "react-router-dom";
import useLeads from "../../Hooks/useLeads";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LeadFilters from "../../components/FbLeadgen/LeadFilters";
import useStages from "../../Hooks/useStages";
import useProjects from "../../Hooks/useProjects";
import useFbForms from "../../Hooks/useFbForms";
import useFbPages from "../../Hooks/useFbPages";
import AppLoader from "../../components/Common/AppLoader";
import DebouncedSearchInput from "../../components/Common/DebouncedSearchInput";
import MobileLeadCards from "../../components/FbLeadgen/MobileLeadCards";
import { getSourceBadgeInfo } from "../../utils/leadSourceHelper";

const NewLeads = () => {
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    source: "all",
    page: 1,
    limit: 20,
    stage: "",
    type: "new",
    projectId: "",
    formId: "",
    pageId: "",
    sortBy: "createdTime",
    sortOrder: "desc",
  });

  const { leads, loading, pagination, fetchNewLeads } = useLeads();
  const [filterModal, setFilterModal] = useState(false);

  const { stages = [] } = useStages({ limit: 1000 });
  const { projects = [] } = useProjects({ limit: 1000 });
  const { forms = [] } = useFbForms({ limit: 1000 });
  const { pages = [] } = useFbPages();

  const handleChange = (name, value) =>
    setFilters((p) => ({ ...p, [name]: value, page: 1 }));

  const handleReset = () =>
    setFilters({
      search: "",
      source: "all",
      page: 1,
      limit: 20,
      stage: "",
      projectId: "",
      formId: "",
      pageId: "",
      sortBy: "createdTime",
      sortOrder: "desc",
    });

  useEffect(() => {
    fetchNewLeads(filters);
  }, [JSON.stringify(filters), refresh]);

  const getLeadDetailsRoute = (lead) => `/lead-details/${lead.source}/${lead._id}`;

  const handleLeadClick = (lead) => {
    try {
      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      sessionStorage.setItem("new_leads_scroll_pos", scrollY.toString());
      sessionStorage.setItem("last_viewed_lead_id", lead._id);
    } catch (e) {}
    navigate(getLeadDetailsRoute(lead));
  };

  useLayoutEffect(() => {
    try {
      const savedPos = sessionStorage.getItem("new_leads_scroll_pos");
      const lastViewedId = sessionStorage.getItem("last_viewed_lead_id");

      const doScroll = () => {
        let scrolledByElement = false;
        if (lastViewedId) {
          const el = document.getElementById(`lead-card-${lastViewedId}`);
          if (el) {
            el.scrollIntoView({ block: "center", behavior: "instant" });
            scrolledByElement = true;
          }
        }
        if (!scrolledByElement && savedPos !== null) {
          const scrollY = parseInt(savedPos, 10);
          window.scrollTo(0, scrollY);
          document.documentElement.scrollTop = scrollY;
          document.body.scrollTop = scrollY;
          const mainContent = document.querySelector(".main-content") || document.querySelector("#layout-wrapper");
          if (mainContent) mainContent.scrollTop = scrollY;
        }
      };

      doScroll();
      requestAnimationFrame(doScroll);
      setTimeout(doScroll, 50);
      setTimeout(doScroll, 150);
      setTimeout(doScroll, 300);

      setTimeout(() => {
        try {
          sessionStorage.removeItem("new_leads_scroll_pos");
          sessionStorage.removeItem("last_viewed_lead_id");
        } catch (e) {}
      }, 500);
    } catch (e) {}
  }, [leads]);

  const formatDateTime = (date) => {
    if (!date) return { date: "-", time: "-" };
    const d = new Date(date);
    return {
      date: d.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      time: d.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  return (
    <div className="page-content">
      <Breadcrumbs title="Leads" breadcrumbItem="New Leads" />

      <div className="card p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div style={{ minWidth: "260px", maxWidth: "400px", width: "100%" }}>
            <div className="position-relative">
              <i
                className="mdi mdi-magnify"
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "12px",
                  fontSize: "16px",
                  color: "#74788d",
                }}
              />
              <DebouncedSearchInput
                placeholder="Search name / email / phone..."
                value={filters.search}
                style={{ paddingLeft: "35px" }}
                onChange={(val) => handleChange("search", val)}
                delay={300}
              />
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div
              className="px-2 py-1 rounded small fw-semibold"
              style={{
                background: "#f8f9fa",
                border: "1px solid #e9ebec",
                minWidth: "70px",
                textAlign: "center",
              }}
            >
              Leads : {pagination?.total || 0}
            </div>

            <Input
              type="select"
              value={filters.limit}
              style={{ width: "95px" }}
              onChange={(e) => handleChange("limit", e.target.value)}
            >
              {[10, 20, 50, 100, 250, 500, 1000, "all"].map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "All" : v}
                </option>
              ))}
            </Input>

            <Button color="primary" onClick={() => setFilterModal(true)}>
              <i className="mdi mdi-filter-variant" />
            </Button>

            <Button color="light" onClick={() => { handleReset(); setRefresh((p) => !p); }}>
              <i className="mdi mdi-refresh" />
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <CardBody>
          {isMobile ? (
            <MobileLeadCards
              leads={leads}
              loading={loading}
              navigate={navigate}
              onLeadClick={handleLeadClick}
              selectedLeadIds={[]}
              handleLeadSelect={() => {}}
              hideCheckbox
            />
          ) : (
            <Table className="table align-middle table-hover" style={{ fontSize: "12px" }}>
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Project</th>
                  <th>Stage</th>
                  <th>Remark</th>
                  <th>Came Date</th>
                  <th>Added Date</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5" style={{ minHeight: "450px" }}>
                      <div style={{ minHeight: "350px" }} className="d-flex align-items-center justify-content-center">
                        <AppLoader compact />
                      </div>
                    </td>
                  </tr>
                ) : leads?.length ? (
                  leads.map((lead) => (
                    <tr
                      key={lead._id}
                      id={`lead-card-${lead._id}`}
                      onClick={() => handleLeadClick(lead)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        {lead.name || "-"} <br />
                        {(() => {
                          const badgeInfo = getSourceBadgeInfo(lead.source);
                          return (
                            <div className={`d-flex align-items-center gap-1 fw-semibold ${badgeInfo.textColor || "text-info"} mb-1`}>
                              <i className={badgeInfo.icon} />
                              <span>{badgeInfo.label}</span>
                            </div>
                          );
                        })()}
                      </td>

                      <td>
                        {lead.email || "-"}
                        <br />
                        {lead.phone || "-"}
                      </td>

                      <td>{lead.projectName || lead.projectId?.projectName || "-"}</td>
                      <td>
                        <span className="badge bg-primary">{lead.stage || "-"}</span>
                      </td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          {lead.remark || "-"}
                        </span>
                      </td>

                      <td style={{ minWidth: "140px" }}>
                        {lead.createdTime ? (() => {
                          const dt = formatDateTime(lead.createdTime);
                          return (
                            <>
                              <div className="fw-semibold">{dt.date}</div>
                              <small className="text-muted">
                                <i className="mdi mdi-clock-outline me-1" />
                                {dt.time}
                              </small>
                            </>
                          );
                        })() : "-"}
                      </td>
                      <td style={{ minWidth: "140px" }}>
                        {lead.syncedAt ? (() => {
                          const dt = formatDateTime(lead.syncedAt);
                          return (
                            <>
                              <div className="fw-semibold">{dt.date}</div>
                              <small className="text-muted">
                                <i className="mdi mdi-clock-outline me-1" />
                                {dt.time}
                              </small>
                            </>
                          );
                        })() : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No New Leads Found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}

          <LeadFilters
            isOpen={filterModal}
            toggle={() => setFilterModal(false)}
            filters={filters}
            setFilters={setFilters}
            stages={stages}
            projects={projects}
            forms={forms}
            pages={pages}
            hideAssignedToFilter={true}
          />

          <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
            <Button
              size="sm"
              color="light"
              disabled={pagination?.page <= 1}
              onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
            >
              <i className="mdi mdi-chevron-left" />
            </Button>

            <span className="small">
              Page {pagination?.page || 1} / {pagination?.totalPages || 1}
            </span>

            <Button
              size="sm"
              color="light"
              disabled={pagination?.page >= pagination?.totalPages}
              onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
            >
              <i className="mdi mdi-chevron-right" />
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default NewLeads;
